import React from 'react';
import { Autocomplete, AutocompleteRenderGetTagProps, Checkbox, TextField } from '@mui/material';
import { L } from 'harmony-language';
import { CustomPopper } from './custom-popper';
import { AgisticsMultiSelectChildren } from './agistics-multi-select-children';

export interface AgisticsMultiItem<T> {
    id: T;
    value: string;
    children?: AgisticsMultiItem<T>[];
}

interface AgisticsMultiSelectProps<T> {
    selectedIds: T[];
    setSelectedIds: (s: T[], a?: T) => void;
    items: AgisticsMultiItem<T>[];
    label?: string;
    presistPrefsChange?: (v: T[]) => void;
    disabled?: boolean;
    required?: boolean;
    confinedWidth?: boolean;
    smallText?: boolean;
    customPopper?: boolean;
    disableClearable?: boolean;
    inputText?: string;
    /**
     * Function that will return styling to be applied to each option in popper.
     */
    styleForOptionsFn?: (optionId: number) => React.CSSProperties;
    /**
     * Function that will return styling to be applied to selected value.
     */
    styleForSelectionsFn?: (optionId: number[]) => React.CSSProperties;
    getOptionDisabled?: (optionId: number) => boolean;
}

/**
 * {@link items} prop must be checked as !undefined before rendering this component
 */
export const AgisticsMultiSelect = <T extends string | number>(props: AgisticsMultiSelectProps<T>) => {
    const {
        selectedIds,
        setSelectedIds,
        items,
        label,
        presistPrefsChange,
        disabled,
        required,
        confinedWidth,
        smallText,
        customPopper,
        disableClearable,
        inputText,
        styleForOptionsFn,
        styleForSelectionsFn,
        getOptionDisabled,
    } = props;
    const selectedValues = React.useMemo(() => items.filter(x => selectedIds.includes(x.id)), [selectedIds]);

    const renderSelected = (selected: AgisticsMultiItem<T>[], getTagProps: AutocompleteRenderGetTagProps) => {
        const tagProps = getTagProps({ index: 1 });

        const selectedStyle = {
            ...(smallText && { fontSize: '.75rem' }),
            ...(styleForSelectionsFn && styleForSelectionsFn(selectedValues.map(v => Number(v.id))))
        }

        const selectedString = confinedWidth ? `[${selected.length.toString()}]` : L.xSelected(selected.length.toString());
        const inputString = inputText ? ' ' + inputText : '';

        return (
            <div
                {...tagProps}
                style={selectedStyle}
            >
                {selectedString + inputString}
            </div>
        );
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            disableClearable={disableClearable}
            disabled={disabled}
            options={items}
            getOptionDisabled={option => getOptionDisabled ? getOptionDisabled(Number(option.id)) : false}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedValues}
            onChange={(event, value) => {
                if (event.type !== 'keydown' && !event.isDefaultPrevented()) {
                    const mappedVal = value.map(x => x.id);
                    setSelectedIds(mappedVal);
                    presistPrefsChange && presistPrefsChange(mappedVal);
                }
            }}
            PopperComponent={customPopper ? CustomPopper : undefined}
            sx={smallText ? { input: { fontSize: '.875rem' }} : undefined}
            renderInput={(params) => {
                return <>
                    <TextField
                        {...params}
                        variant='standard'
                        label={label}
                        required={required && selectedValues.length === 0}
                    />
                </>
            }}
            renderTags={renderSelected}
            renderOption={(props, option) => {
                const styleForOptions = styleForOptionsFn && styleForOptionsFn(Number(option.id));
                
                return (
                    <li
                        {...props}
                        // props passes a key that is value, which might not be unique (users/contacts with same name)
                        key={option.id}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ...styleForOptions }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox checked={selectedIds.indexOf(option.id) > -1} style={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }} />
                            {option.value}
                        </div>
                        {option.children && <AgisticsMultiSelectChildren
                            selectedValues={selectedValues}
                            option={option}
                            setSelectedIds={setSelectedIds}
                        >
                            {option.children}
                        </AgisticsMultiSelectChildren>}
                    </li>
                )
            }}
        />
    );
};
